/**
 * Curated refine prompts offered in the Script Writer's Refine tab.
 *
 * Picking an entry drops its `prompt` into the refine box as ordinary editable
 * feedback text — it is not a separate execution path, so everything downstream
 * (`in_feedback`, the edit-mode agent run, `in_feedback_history`) behaves exactly
 * as it does for hand-typed feedback.
 *
 * The `id` is the stable key: it is the `data-testid` suffix and the i18n lookup
 * key, so it must never change once shipped. Only the TITLE is localized, under
 * `canvas.scriptWriterNode.promptLibrary.<id>` — the prompt BODY stays English on
 * purpose. Refine runs are an edit path, and per the content-language rules an
 * edit instruction must anchor to the existing screenplay and never name a
 * language; a translated instruction reads as a licence to translate a script the
 * user already approved. The user can of course rewrite the inserted text in any
 * language they like.
 */

export interface ScriptPromptLibraryEntry {
  /** Stable id — i18n key suffix and test id. Never change once shipped. */
  id: string
  /** English fallback title, shown when a locale has no entry for `id`. */
  title: string
  /** Instruction inserted into the refine box. English by design (see above). */
  prompt: string
}

export const SCRIPT_PROMPT_LIBRARY: readonly ScriptPromptLibraryEntry[] = [
  {
    id: 'coldOpen',
    // i18n-exempt: English fallback only — displayed copy comes from canvas.scriptWriterNode.promptLibrary.<id> via localizeScriptPromptTitle.
    title: 'Enhance the Cold Open / Opening Hook',
    prompt:
      'Rewrite the following opening scene to sharpen the hook. Increase the immediate narrative stakes, drop us directly into the action (in media res), and ensure the final line of the scene forces the reader/audience to ask an urgent question. Maintain industry standard screenplay format, character voices, and established tone.',
  },
  {
    id: 'cliffhanger',
    // i18n-exempt: English fallback only — displayed copy comes from canvas.scriptWriterNode.promptLibrary.<id> via localizeScriptPromptTitle.
    title: 'Raise the Scene Cliffhanger',
    prompt:
      'Refine the end of this scene to create a powerful cliffhanger. Escalate the dramatic conflict, introduce an unexpected twist or realization, and cut the scene at the moment of highest tension. Keep all existing character arcs and world rules intact.',
  },
  {
    id: 'subtext',
    // i18n-exempt: English fallback only — displayed copy comes from canvas.scriptWriterNode.promptLibrary.<id> via localizeScriptPromptTitle.
    title: 'Subtext & Unsaid Dialogue',
    prompt:
      'Rewrite this dialogue scene to add subtext. Characters should NOT state their true feelings directly. Use passive aggression, deflection, double meanings, and physical micro-actions (action beats) to reveal what they actually want beneath the surface. Preserve the core scene objective.',
  },
  {
    id: 'pacing',
    // i18n-exempt: English fallback only — displayed copy comes from canvas.scriptWriterNode.promptLibrary.<id> via localizeScriptPromptTitle.
    title: 'Pacing & Tension Escalation',
    prompt:
      "Tighten the pacing of this sequence to build suspense. Trim unnecessary exposition, shorten the sentences in the action lines to create a fast cinematic rhythm, and make the antagonist's pressure on the protagonist feel immediate and relentless.",
  },
  {
    id: 'characterVoice',
    // i18n-exempt: English fallback only — displayed copy comes from canvas.scriptWriterNode.promptLibrary.<id> via localizeScriptPromptTitle.
    title: 'Character Voice Differentiation',
    prompt:
      'Analyze and rewrite the dialogue in this scene to give each character a distinct voice. Adjust vocabulary, sentence length, cadence, and speech patterns so that if character names were removed, the reader could still tell who is speaking based on personality and background.',
  },
  {
    id: 'showDontTell',
    // i18n-exempt: English fallback only — displayed copy comes from canvas.scriptWriterNode.promptLibrary.<id> via localizeScriptPromptTitle.
    title: '"Show, Don\'t Tell" Visual Action',
    prompt:
      'Convert all internal exposition and dialogue-heavy explanations in this scene into pure visual storytelling. Replace character explanations with physical reactions, environmental interaction, symbolic props, and visual actions.',
  },
  {
    id: 'raiseStakes',
    // i18n-exempt: English fallback only — displayed copy comes from canvas.scriptWriterNode.promptLibrary.<id> via localizeScriptPromptTitle.
    title: 'Raising the Stakes (Midpoint / Turning Point)',
    prompt:
      'Elevate the dramatic stakes in this scene. Force the protagonist into a "dilemma choice" — a decision where every option has a severe cost. Ensure the outcome permanently alters the direction of the story and makes returning to the status quo impossible.',
  },
  {
    id: 'trimTheFat',
    // i18n-exempt: English fallback only — displayed copy comes from canvas.scriptWriterNode.promptLibrary.<id> via localizeScriptPromptTitle.
    title: 'Trimming the Fat (Scene Compression)',
    prompt:
      'Compress this scene by 30% while retaining all essential plot points and emotional beats. Cut late-entry setups and early-exit fluff ("enter late, leave early"). Punch up the remaining action blocks and dialogue for maximum impact.',
  },
  {
    id: 'emotionalResonance',
    // i18n-exempt: English fallback only — displayed copy comes from canvas.scriptWriterNode.promptLibrary.<id> via localizeScriptPromptTitle.
    title: 'Emotional Resonance & Vulnerability',
    prompt:
      "Refine this character moment to deepen the emotional punch. Heighten the character's internal vulnerability, ground their reaction in a specific tactile detail or memory, and allow the emotional beat to land before moving to the next action beat.",
  },
  {
    id: 'loglinePolish',
    // i18n-exempt: English fallback only — displayed copy comes from canvas.scriptWriterNode.promptLibrary.<id> via localizeScriptPromptTitle.
    title: 'Pitch / Tagline / Logline Polish',
    prompt:
      'Analyze this script excerpt/synopsis and generate 3 high-concept loglines, a gripping 1-sentence tagline, and a 3-sentence elevator pitch. Focus on the core protagonist, their inciting incident, the primary obstacle, and the ultimate stakes.',
  },
]

/**
 * Localize an entry's display title, falling back to the English `title`.
 * Mirrors the value/label split used by {@link localizeNodeBgColor}.
 */
export function localizeScriptPromptTitle(
  entry: ScriptPromptLibraryEntry,
  t: (key: string, opts?: { defaultValue?: string }) => string,
): string {
  return t(`scriptWriterNode.promptLibrary.${entry.id}`, { defaultValue: entry.title })
}
